import { trackButtonClicked, trackModalViewed } from '@heidi/analytics/events';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { Dialog, DialogContent, DialogTitle } from '@heidi/design-system/dialog';
import { Skeleton } from '@heidi/design-system/skeleton';
import { useAtom } from 'jotai';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { heidi2OptInModalOpenAtom } from '../../../data/heidi-2';

import { heidi2OptInModalMessages } from './heidi-2-opt-in-modal.messages';

const Heidi2OptInModalLoading = () => (
  <div className="flex h-[572px] flex-col gap-6 p-8" aria-hidden="true">
    <Skeleton className="h-52 w-full rounded-xl" />
    <Skeleton className="h-16 w-full rounded-lg" />
    <Skeleton className="h-28 w-full rounded-lg" />
  </div>
);

// `React.lazy` rather than `next/dynamic`: this zone may not reach for Next
// beyond `next/image`, and the dialog only renders client-side anyway.
const Heidi2OptInModalContent = lazy(() =>
  import('./heidi-2-opt-in-modal-content').then((module) => ({
    default: module.Heidi2OptInModalContent,
  }))
);

export function Heidi2OptInModal() {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useAtom(heidi2OptInModalOpenAtom);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const viewTrackedRef = useRef(false);
  const trackDismiss = useCallback(() => {
    trackButtonClicked({
      screen_name: ScreenName.Heidi2OptInModal,
      action: ButtonAction.Heidi2OptInModalDismiss,
      label: 'dismiss',
    });
  }, []);

  useEffect(() => {
    if (!isDialogVisible) {
      viewTrackedRef.current = false;
      return;
    }
    if (viewTrackedRef.current) return;

    viewTrackedRef.current = true;
    trackModalViewed({ screenName: ScreenName.Heidi2OptInModal });
  }, [isDialogVisible]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={setIsOpen}
      onOpenChangeComplete={setIsDialogVisible}
    >
      <DialogContent
        aria-describedby={undefined}
        className="scrollbar-subtle h-[596px] max-h-[90dvh] max-w-[500px] gap-0 overflow-y-auto p-0"
        closeIconClassName="text-text-primary-on-light"
        onCloseIconClick={trackDismiss}
        onEscapeKeyDown={trackDismiss}
        onPointerDownOutside={trackDismiss}
      >
        <DialogTitle className="sr-only">
          {intl.formatMessage(heidi2OptInModalMessages.title)}
        </DialogTitle>
        {isDialogVisible ? (
          <Suspense fallback={<Heidi2OptInModalLoading />}>
            <Heidi2OptInModalContent />
          </Suspense>
        ) : (
          <Heidi2OptInModalLoading />
        )}
      </DialogContent>
    </Dialog>
  );
}
